import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Gear, PencilSimple, Trash, Check, X, LockSimple, Plus } from "@phosphor-icons/react";

export default function CategoryManager({ onChanged }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState([]);
  const [editing, setEditing] = useState(null);
  const [draft, setDraft] = useState("");
  const [newName, setNewName] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    const { data } = await api.get("/categories/manage");
    setRows(data);
  };
  useEffect(() => { if (open) { load(); setEditing(null); setNewName(""); } }, [open]);

  const create = async () => {
    if (!newName.trim()) return;
    setBusy(true);
    try { await api.post("/categories", { name: newName.trim() }); toast.success("Category added"); setNewName(""); await load(); onChanged?.(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  const rename = async (oldName) => {
    if (!draft.trim() || draft.trim() === oldName) { setEditing(null); return; }
    setBusy(true);
    try { await api.patch("/categories", { old_name: oldName, new_name: draft.trim() }); toast.success("Category renamed"); setEditing(null); await load(); onChanged?.(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  const remove = async (name) => {
    setBusy(true);
    try { const { data } = await api.post("/categories/delete", { name }); toast.success(data.moved ? `Deleted — ${data.moved} item(s) moved to Other` : "Category deleted"); await load(); onChanged?.(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" data-testid="manage-categories-btn" className="gap-2"><Gear size={17} weight="bold" /> Categories</Button>
      </DialogTrigger>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="category-manager">
        <DialogHeader><DialogTitle className="font-heading">Manage categories</DialogTitle></DialogHeader>

        <div className="flex gap-2 py-2">
          <Input data-testid="new-category-input" value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Add a new category" onKeyDown={(e) => e.key === "Enter" && create()} />
          <Button onClick={create} disabled={busy} data-testid="add-category-btn" className="bg-primary hover:bg-primary/90 gap-1.5"><Plus size={15} /> Add</Button>
        </div>

        <div className="border border-border rounded-md divide-y divide-border">
          {rows.map((c) => (
            <div key={c.name} data-testid={`cat-row-${c.name}`} className="flex items-center gap-3 px-4 py-2.5">
              {editing === c.name ? (
                <>
                  <Input autoFocus value={draft} onChange={(e) => setDraft(e.target.value)} onKeyDown={(e) => e.key === "Enter" && rename(c.name)} className="h-8" data-testid="rename-input" />
                  <Button size="icon" variant="ghost" className="h-8 w-8 text-success" onClick={() => rename(c.name)} data-testid="rename-save"><Check size={16} /></Button>
                  <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setEditing(null)}><X size={16} /></Button>
                </>
              ) : (
                <>
                  <span className="flex-1 text-sm font-medium">{c.name}</span>
                  <span className="text-xs text-muted-foreground">{c.count} item{c.count === 1 ? "" : "s"}</span>
                  {c.name !== "Other" ? (
                    <>
                      <Button size="icon" variant="ghost" className="h-8 w-8 hover:text-primary" data-testid={`edit-cat-${c.name}`} onClick={() => { setEditing(c.name); setDraft(c.name); }}><PencilSimple size={15} /></Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button size="icon" variant="ghost" className="h-8 w-8 hover:text-destructive" data-testid={`delete-cat-${c.name}`}><Trash size={15} /></Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete "{c.name}"?</AlertDialogTitle>
                            <AlertDialogDescription>{c.count > 0 ? `${c.count} item(s) using this category will be moved to "Other".` : "This category has no items."}</AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction onClick={() => remove(c.name)} className="bg-destructive hover:bg-destructive/90">Delete</AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </>
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[11px] text-muted-foreground" title="Fallback category"><LockSimple size={12} /> Fallback</span>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
        <p className="text-xs text-muted-foreground mt-1">Rename or delete any category. Renaming updates all its items; deleting moves its items to "Other".</p>
      </DialogContent>
    </Dialog>
  );
}
